import { NavigationHeader } from "@/components";
import { ImageLightboxModal } from "@/components/library/ImageLightboxModal";
import { ScrollManager } from "@/components/ui";
import { Button } from "@/components/ui/button";
import ChatPage from "@/pages/chat/ChatPage";
import { LibraryPage } from "@/pages/library/LibraryPage";
import {
  SidebarInset,
  SidebarProvider,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ProjectsNavBar } from "@/components/workspace";
import { AppSidebar } from "@/features/sidebar/components/app-sidebar";
import { SidebarDndProvider } from "@/contexts/SidebarDndProvider";
import { ThreadMoveProvider } from "@/contexts/ThreadMoveContext";
import { useNewChat } from "@/hooks/useNewChat";
import { supabase } from "@/lib";
import { cn } from "@/lib/utils";
import { useAssistantStore, useThreadStore } from "@/store";
import useAuthStore from "@/store/authStore";
import { PanelLeft } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

const ChatTopBar = ({ state, setState }) => {
  const location = useLocation();
  const isToolRoute = location.pathname.includes("/chat/tool/");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Track window size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleToggleContextPanel = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isContextPanelOpen: !prev.isContextPanelOpen,
    }));
  }, [setState]);

  return (
    <>
      <NavigationHeader
        onToggleContextPanel={handleToggleContextPanel}
        className={cn("flex-1", isMobile && "px-2")}
      />
    </>
  );
};

const LibraryTopBar = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { toggleSidebar } = useSidebar();

  // Platform detection for keyboard shortcuts
  const isMac =
    typeof window !== "undefined" &&
    navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  const modifierKey = isMac ? "⌘" : "Ctrl";

  // Track window size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className={cn("flex-1 flex items-center gap-2", isMobile && "px-2")}>
      {/* Mobile sidebar toggle - only visible on narrow screens */}
      {isMobile && (
        <TooltipProvider>
          <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-full flex-shrink-0"
                onClick={() => {
                  setTooltipOpen(false);
                  toggleSidebar();
                }}
                aria-label="Toggle sidebar"
              >
                <PanelLeft className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Toggle Sidebar ({modifierKey}B)</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <h1 className="text-2xl font-bold tracking-tight">Library</h1>
    </div>
  );
};

const DocumentsTopBar = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { toggleSidebar } = useSidebar();

  // Platform detection for keyboard shortcuts
  const isMac =
    typeof window !== "undefined" &&
    navigator.platform.toUpperCase().indexOf("MAC") >= 0;
  const modifierKey = isMac ? "⌘" : "Ctrl";

  // Track window size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className={cn("flex-1 flex items-center gap-2", isMobile && "px-2")}>
      {/* Mobile sidebar toggle - only visible on narrow screens */}
      {isMobile && (
        <TooltipProvider>
          <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 rounded-full flex-shrink-0"
                onClick={() => {
                  setTooltipOpen(false);
                  toggleSidebar();
                }}
                aria-label="Toggle sidebar"
              >
                <PanelLeft className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Toggle Sidebar ({modifierKey}B)</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      <h1 className="text-2xl font-bold tracking-tight">Documents</h1>
    </div>
  );
};

const ImageLightboxTopBar = () => {
  const [imageTitle, setImageTitle] = useState("Generated Image");

  // Listen for image data updates from the ImageLightboxPage
  useEffect(() => {
    const handleImageData = (event) => {
      if (event.detail && event.detail.prompt) {
        setImageTitle(event.detail.prompt);
      }
    };

    window.addEventListener("imageLightboxData", handleImageData);
    return () =>
      window.removeEventListener("imageLightboxData", handleImageData);
  }, []);

  return (
    <div className="flex items-center w-full h-full">
      <div className="font-medium text-base truncate max-w-3xl">
        {imageTitle}
      </div>
    </div>
  );
};

export function MainLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isChatRoute = location.pathname.includes("/chat");
  const isWorkspaceRoute =
    location.pathname.includes("/projects") ||
    location.pathname.includes("/workspaces");
  const isLibraryRoute = location.pathname === "/library";
  const isDocumentsRoute = location.pathname === "/documents";
  const isImageLightboxRoute = location.pathname.includes("/library/image/");
  const isToolRoute = location.pathname.includes("/chat/tool/");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Track window size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [state, setState] = useState({
    activeTab: "chat",
    hasInitializedEditor: false,
    isContextPanelOpen: false,
  });

  // Near the top of MainLayoutContent or in a new useEffect in that component
  useEffect(() => {
    // Flag to prevent multiple initializations in a short time
    let isHandlingAuthChange = false;
    let lastHandledEvent = null;
    let lastHandledTime = 0;

    // Debounce for authstate changes
    const debounceAuthChanges = (event, session, callback) => {
      // Check for duplicate events in quick succession
      const now = Date.now();
      const isDuplicate =
        lastHandledEvent === event && now - lastHandledTime < 3000; // 3 second debounce

      if (isDuplicate) {
        return;
      }

      // Track this event
      lastHandledEvent = event;
      lastHandledTime = now;

      // Process the event
      callback(event, session);
    };

    // Listen for auth state changes
    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Debounce the auth state changes
        debounceAuthChanges(event, session, async (event, session) => {
          // Prevent loops and redundant operations
          if (isHandlingAuthChange) {
            return;
          }

          // Check if this was just processed by AuthCallback
          let isAuthCallbackProcessed = false;
          try {
            isAuthCallbackProcessed =
              sessionStorage.getItem("auth_callback_processed") === "true";
            const authCallbackTimestamp = parseInt(
              sessionStorage.getItem("auth_callback_timestamp") || "0"
            );
            const isRecent = Date.now() - authCallbackTimestamp < 10000; // Within last 10 seconds
            isAuthCallbackProcessed = isAuthCallbackProcessed && isRecent;
          } catch (e) {
            console.warn("Error checking auth_callback_processed flag:", e);
          }

          if (
            isAuthCallbackProcessed &&
            (event === "SIGNED_IN" || event === "TOKEN_REFRESHED")
          ) {
            return;
          }

          try {
            isHandlingAuthChange = true;

            if (event === "SIGNED_OUT" || event === "USER_DELETED") {
              // Clear any persisted data
              localStorage.removeItem("auth-storage");
              localStorage.removeItem("assistant-storage");
              localStorage.removeItem("thread-storage");

              // Check if this is an intentional logout (user clicked logout button)
              const isIntentionalLogout =
                localStorage.getItem("intentional_logout");

              if (isIntentionalLogout) {
                // User clicked logout - clear the flag and let the logout handler navigate
                localStorage.removeItem("intentional_logout");
                // Don't navigate here - let the logout handler in the component handle it
              } else {
                // Session expired or other automatic logout - redirect to login
                if (!location.pathname.includes("/auth/")) {
                  navigate("/auth/login", { replace: true });
                }
              }
            } else if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
              // No need to manually initialize here - the auth callback will handle it
            }
          } finally {
            // Use setTimeout to ensure we don't set this back to false too quickly
            setTimeout(() => {
              isHandlingAuthChange = false;
            }, 1000);
          }
        });
      }
    );

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [navigate, location.pathname]);

  return (
    <SidebarProvider className="z-[250]">
      {/* CRITICAL: ScrollManager is completely disabled for chat routes */}
      {!isChatRoute && <ScrollManager />}
      {/* ThreadMoveProvider and SidebarDndProvider wrap both sidebar and main content
          to enable drag-and-drop between sidebar and project page drop zones */}
      <ThreadMoveProvider>
        <SidebarDndProvider>
          <div className="flex h-screen w-full overflow-hidden">
            <AppSidebar />
            <MainLayoutContent
              isChatRoute={isChatRoute}
              isWorkspaceRoute={isWorkspaceRoute}
              isLibraryRoute={isLibraryRoute}
              isDocumentsRoute={isDocumentsRoute}
              isImageLightboxRoute={isImageLightboxRoute}
              isToolRoute={isToolRoute}
              isMobile={isMobile}
              state={state}
              setState={setState}
            >
              {children}
            </MainLayoutContent>
          </div>
        </SidebarDndProvider>
      </ThreadMoveProvider>
    </SidebarProvider>
  );
}

// Extracted inner component that uses the sidebar state
function MainLayoutContent(props) {
  const {
    isChatRoute,
    isWorkspaceRoute,
    isLibraryRoute,
    isDocumentsRoute,
    isImageLightboxRoute,
    isToolRoute,
    isMobile,
    state,
    setState,
    children,
  } = props;
  const { state: sidebarState } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();

  // New chat functionality for collapsed sidebar
  const { user, currentWorkspace } = useAuthStore();
  const { currentAssistant } = useAssistantStore();
  const { clearCurrentThread } = useThreadStore();
  const navigatingRef = useRef(false);

  // Use centralized new chat hook - no need for sidebar functions since we use global events
  const { handleNewChat, isCreatingChat } = useNewChat();

  // Check for image lightbox modal mode (navigated from Library or Chat with background state)
  const backgroundLocation = location.state?.backgroundLocation;
  const imageData = location.state?.imageData;
  const allImages = location.state?.allImages;
  const isImageLightboxModal =
    isImageLightboxRoute && backgroundLocation;

  // Determine if we should render LibraryPage directly to keep it mounted during modal
  // This prevents LibraryPage from unmounting/remounting when opening/closing the modal
  const isModalFromLibrary =
    isImageLightboxModal && backgroundLocation?.pathname === "/library";
  const shouldRenderLibraryDirectly = isLibraryRoute || isModalFromLibrary;

  // Detect if modal opened from chat (for header display)
  const isModalFromChat =
    isImageLightboxModal && backgroundLocation?.pathname?.startsWith("/chat/");

  // Only render ChatPage directly when actually on a chat route
  // TODO: Add instant return support for chat->modal->chat flow (currently reloads thread)
  const shouldRenderChatDirectly = isChatRoute;

  // Redirect /chat to /chat/new (MainLayout bypasses router's Navigate)
  useEffect(() => {
    if (location.pathname === "/chat") {
      navigate("/chat/new", { replace: true });
    }
  }, [location.pathname, navigate]);

  // Close modal handler - navigate back to background location
  const handleCloseModal = useCallback(() => {
    if (backgroundLocation) {
      navigate(backgroundLocation.pathname, { replace: true });
    } else {
      navigate("/library", { replace: true });
    }
  }, [backgroundLocation, navigate]);

  return (
    <>
    <SidebarInset className="transition-all duration-200 md:!m-0 md:!rounded-none md:!shadow-none">
      <div className="flex flex-col h-full w-full overflow-hidden bg-transparent">
        <header
          className={cn(
            "flex shrink-0 items-center",
            !isWorkspaceRoute && "border-b border-transparent",
            "bg-transparent w-full",
            "border-border/0",
            !isToolRoute && isMobile
              ? "h-14 sm:h-14 md:h-16 py-1"
              : !isToolRoute && "h-12 sm:h-14 md:h-16",
            isToolRoute && "hidden",
            "z-[150]",
            "transition-all duration-200"
          )}
        >
          <div
            className={cn(
              "flex items-center w-full",
              !isToolRoute && "h-full",
              "transition-all duration-200"
            )}
          >
            {(isChatRoute || isModalFromChat) && (
              <div
                className={cn(
                  "flex-1 flex items-center",
                  isMobile ? "pl-3 pr-2 py-2" : "pl-4",
                  "transition-all duration-200"
                )}
              >
                <ChatTopBar state={state} setState={setState} />
              </div>
            )}

            {(isLibraryRoute || isModalFromLibrary) && (
              <div
                className={cn(
                  "flex-1 flex items-center",
                  isMobile ? "pl-3 pr-2 py-2" : "pl-4",
                  "transition-all duration-200"
                )}
              >
                <LibraryTopBar />
              </div>
            )}

            {isDocumentsRoute && (
              <div
                className={cn(
                  "flex-1 flex items-center",
                  isMobile ? "pl-3 pr-2 py-2" : "pl-4",
                  "transition-all duration-200"
                )}
              >
                <DocumentsTopBar />
              </div>
            )}

            {isImageLightboxRoute && !isModalFromLibrary && !isModalFromChat && (
              <div
                className={cn(
                  "flex-1 flex items-center",
                  isMobile ? "px-3 py-2" : "px-4",
                  "transition-all duration-200"
                )}
              >
                <ImageLightboxTopBar />
              </div>
            )}

            {isWorkspaceRoute && (
              <div
                className={cn(
                  "flex-1 flex items-center",
                  "w-full",
                  sidebarState === "collapsed" ? "pl-1" : "pl-2",
                  isMobile ? "pl-4" : "pl-2",
                  "transition-all duration-300 ease-in-out"
                )}
              >
                <ProjectsNavBar />
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 w-full h-full overflow-hidden">
          <div className="flex flex-col h-full w-full max-w-[1200px] mx-auto">
            {/* Render LibraryPage directly to keep it mounted during modal transitions */}
            {/* For chat->modal, we don't render anything behind - modal covers screen, thread reloads on close */}
            {shouldRenderLibraryDirectly ? (
              <LibraryPage />
            ) : shouldRenderChatDirectly ? (
              <ChatPage isNewChat={location.pathname === "/chat/new"} />
            ) : isModalFromChat ? (
              // Modal from chat - don't render Outlet (would double-render lightbox)
              // The modal covers the screen, thread will reload when modal closes
              null
            ) : (
              children || <Outlet />
            )}
          </div>
        </main>
      </div>
    </SidebarInset>

    {/* Image lightbox modal - renders on top when navigating from Library/Chat */}
    {isImageLightboxModal && (
      <ImageLightboxModal
        imageData={imageData}
        allImages={allImages}
        onClose={handleCloseModal}
      />
    )}
    </>
  );
}

export default MainLayout;
